import React, { useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import CardStatus from '../../../../shared/components/CardStatus/CardStatus';
import EmptyCard from '../../../../shared/components/v3/Cards/EmptyCard/EmptyCard';
import ErrorCard from '../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import StyledTable from '../../../../shared/components/v3/Table/StyledTable/StyledTable';
import TablePaginationV3 from '../../../../shared/components/v3/TablePagination/TablePagination';
import { MAX_COLLABORATORS } from '../../../../shared/constants/collaborators';
import { DEFAULT_PAGINATOR_OPTIONS_V3 } from '../../../../shared/constants/constants';
import { SECTION_COLLABORATORS } from '../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { COLLABORATORS_UNSUBSCRIPTIONS_TABLE_COLUMNS } from '../../../../shared/handlers/tableColumns';
import { useIsMounted } from '../../../../shared/hooks/useIsMounted';
import { IUnsubscribedMembersData } from '../../../../shared/interfaces/collaborators';
import { IUnsubscribedMembersResponse } from '../../../../shared/interfaces/responseInterface';
import Companies from '../../../../shared/services/api/collaborators/collaborators';
import { TRANSLATION_COLLABORATORS } from '../../../../shared/translations/collaborators';
import { TRANSLATIONS_COLLABORATORS_DETAIL } from '../../../../shared/translations/collaboratorsDetail';
import { TRANSLATIONS_GLOBAL } from '../../../../shared/translations/commons';
import { sortArray } from '../../../../shared/utils/utils';
import TableFilter from '../TableFilter/TableFilter';

import './CollaboratorsUnsubscriptionsTable.scss';

const CollaboratorsUnsubscriptionsTable = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [collaboratorsUnsubscribed, setCollaboratorsUnsubscribed] = useState<IUnsubscribedMembersData[] | undefined>(
    undefined
  );
  const [paginatorState, setPaginatorState] = useState<number>(150);
  const [actualPage, setActualPage] = useState<number>(0);
  const [currentPageData, setCurrentPageData] = useState<IUnsubscribedMembersData[]>([]);
  const [error, setError] = useState(false);
  const [emptySearch, setEmptySearch] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const { company } = useContext(CompanyContext);
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const onSearch = async (searchValue?: string) => {
    setLoading(true);
    try {
      let response: IUnsubscribedMembersResponse;
      setError(false);
      setFiltersApplied(searchValue !== undefined);
      if (company.id !== '') {
        response = await Companies.getUnsubscribedMembers(company.id, searchValue?.toLowerCase());
        setEmptySearch(!response.members.length);
        if (isMounted) {
          const sorted = [...response.members].sort((a, b) => a.email.localeCompare(b.email));
          setCollaboratorsUnsubscribed(sorted);
        }
      }
    } catch {
      setError(true);
    }
  };

  const getTotalCollaboratorsUnsubscribed = () => {
    return collaboratorsUnsubscribed ? collaboratorsUnsubscribed.length : 0;
  };

  const paginateCollaborators = (data: any[] = []) => {
    const from = actualPage * paginatorState;
    const to = from + paginatorState;
    const pageData = data.slice(from, to);
    setCurrentPageData(pageData || []);
    setLoading(false);
  };

  const sortData = (columnKey: string, isAscending: boolean) => {
    if (collaboratorsUnsubscribed) {
      const sortedData = sortArray(collaboratorsUnsubscribed, columnKey, isAscending);
      paginateCollaborators(sortedData);
    }
  };

  useEffect(() => {
    setActualPage(0);
  }, [collaboratorsUnsubscribed, paginatorState]);

  useEffect(() => {
    if (collaboratorsUnsubscribed) {
      paginateCollaborators(collaboratorsUnsubscribed);
    } else {
      setCurrentPageData([]);
    }
  }, [collaboratorsUnsubscribed, actualPage, paginatorState]);

  useEffect(() => {
    if (isMounted) {
      onSearch();
    }
  }, [company, isMounted]);

  const renderEmptyState = () => {
    if (error) {
      return (
        <div className="empty-unsubscribed-collaborators">
          <ErrorCard
            primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
            secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
            action={() => onSearch()}
            buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
          />
        </div>
      );
    }

    if (emptySearch) {
      return (
        <div className="empty-unsubscribed-collaborators">
          {filtersApplied ? (
            <CardStatus
              type="informative"
              message={t(TRANSLATIONS_COLLABORATORS_DETAIL.collaboratorsTable.empty.noResults)}
            />
          ) : (
            <EmptyCard
              primaryText={t(TRANSLATION_COLLABORATORS.table.emptyData.deletedTable.title)}
              secondaryText={t(TRANSLATION_COLLABORATORS.table.emptyData.deletedTable.description)}
            />
          )}
        </div>
      );
    }
  };

  return (
    <div className="collaborators-unsubscribed-home">
      <TableFilter
        onSearch={onSearch}
        loading={loading}
        searchBarPlaceholder={TRANSLATION_COLLABORATORS.table.searchBarPlaceholderUnsubscriptions}
      />
      {renderEmptyState() || (
        <StyledTable
          data={currentPageData}
          sortData={sortData}
          loading={loading}
          columns={COLLABORATORS_UNSUBSCRIPTIONS_TABLE_COLUMNS}
          dataKey="email"
        />
      )}
      {collaboratorsUnsubscribed && collaboratorsUnsubscribed.length >= MAX_COLLABORATORS && (
        <div className="info-row">
          <CardStatus type="informative" message={t(TRANSLATIONS_COLLABORATORS_DETAIL.maxMembersInfo)} />
        </div>
      )}
      {collaboratorsUnsubscribed && (
        <div className="paginator-container">
          <TablePaginationV3
            rowsPerPageOptions={DEFAULT_PAGINATOR_OPTIONS_V3}
            rowsPerPage={paginatorState}
            count={getTotalCollaboratorsUnsubscribed()}
            actualPage={actualPage}
            onPageChange={setActualPage}
            onRowsPerPageChange={setPaginatorState}
            sectionPage={SECTION_COLLABORATORS}
            marginTop={20}
          />
        </div>
      )}
    </div>
  );
};

export default CollaboratorsUnsubscriptionsTable;
